// src/components/LinkList.js

import React, { useEffect, useState } from 'react';
import axios from '../utils/axios';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Tooltip, Typography, Box, Snackbar, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Stack, Chip, Collapse
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import LinkIcon from '@mui/icons-material/Link';
import BadgeIcon from '@mui/icons-material/Badge';
import NumbersIcon from '@mui/icons-material/Numbers';
import DateRangeIcon from '@mui/icons-material/DateRange';
import ClickIcon from '@mui/icons-material/Mouse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import config from '../config';
import { QRCodeCanvas } from 'qrcode.react';

export default function LinkList() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState({ open: false, text: '', severity: 'success' });
  const [error, setError] = useState('');
  const [editLink, setEditLink] = useState(null);
  const [editForm, setEditForm] = useState({ link_name: '', original_url: '', note: '' });
  const [saving, setSaving] = useState(false);
  const [showTable, setShowTable] = useState(false); // <-- trạng thái ẩn/hiện

  // Fetch links từ API
  useEffect(() => {
    axios.get('/my-links')
      .then(res => setLinks(Array.isArray(res.data.links) ? res.data.links : []))
      .catch(() => {
        setError('Lỗi khi lấy danh sách link!');
        setLinks([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleCopy = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        setSnack({ open: true, text: 'Đã copy link!', severity: 'success' });
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setSnack({ open: true, text: 'Đã copy link!', severity: 'success' });
      }
    } catch {
      setSnack({ open: true, text: 'Copy không thành công!', severity: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bạn chắc chắn muốn xóa link này?')) return;
    try {
      await axios.delete(`/my-links/${id}`);
      setLinks((prevLinks) => prevLinks.filter(l => l?.id !== id));
      setSnack({ open: true, text: 'Đã xóa link!', severity: 'success' });
    } catch {
      setSnack({ open: true, text: 'Xóa link thất bại!', severity: 'error' });
    }
  };

  const handleEdit = (link) => {
    setEditForm({
      link_name: link?.link_name || '',
      original_url: link?.original_url || '',
      note: link?.note || ''
    });
    setEditLink(link);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`/my-links/${editLink?.id}`, editForm);
      setLinks((prevLinks) =>
        prevLinks.map(l => (l?.id === editLink?.id ? res.data.link : l))
      );
      setSnack({ open: true, text: 'Đã cập nhật link!', severity: 'success' });
      setEditLink(null);
    } catch {
      setSnack({ open: true, text: 'Cập nhật link thất bại!', severity: 'error' });
    }
    setSaving(false);
  };

  if (loading) return (
    <Box sx={{ mt: 4, textAlign: 'center' }}>
      <CircularProgress />
      <Typography sx={{ mt: 2 }}>Đang tải danh sách link...</Typography>
    </Box>
  );

  if (error) return (
    <Box sx={{ mt: 4, textAlign: 'center' }}>
      <Alert severity="error">{error}</Alert>
    </Box>
  );

  if (!Array.isArray(links) || links.length === 0) return (
    <Box sx={{ mt: 4, textAlign: 'center' }}>
      <Typography variant="h6">Bạn chưa có link rút gọn nào!</Typography>
    </Box>
  );

  return (
    <Box sx={{ mt: 6 }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        mb: 2,
        gap: 1,
        justifyContent: 'center'
      }}>
        <Button
          variant="contained"
          color={showTable ? "secondary" : "primary"}
          startIcon={showTable ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={() => setShowTable(v => !v)}
          sx={{
            borderRadius: 99,
            fontWeight: 800,
            fontSize: 18,
            px: 3,
            boxShadow: "0 2px 12px #6366f144"
          }}
        >
          {showTable ? 'Ẩn danh sách link' : 'Hiện danh sách link'}
        </Button>
      </Box>

      <Collapse in={showTable}>
        <TableContainer component={Paper} sx={{
          maxWidth: 1200, margin: "auto", boxShadow: 4, background: "#fafcff", mb: 3
        }}>
          <Table>
            <TableHead>
              <TableRow sx={{ background: "#1976d2" }}>
                <TableCell sx={{ fontWeight: 700, color: "#fff" }}>
                  <BadgeIcon sx={{ mr: 0.5, fontSize: 20, verticalAlign: 'middle' }} />
                  Tên/Nhận diện
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#fff" }}>
                  <LinkIcon sx={{ mr: 0.5, fontSize: 20, verticalAlign: 'middle' }} />
                  Link gốc
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#fff" }}>
                  <NumbersIcon sx={{ mr: 0.5, fontSize: 20, verticalAlign: 'middle' }} />
                  Link rút gọn
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: "#fff" }}>
                  <ClickIcon sx={{ mr: 0.5, fontSize: 20, verticalAlign: 'middle' }} />
                  Số click
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: "#fff" }}>
                  <DateRangeIcon sx={{ mr: 0.5, fontSize: 20, verticalAlign: 'middle' }} />
                  Ngày tạo
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: "#fff" }}>
                  <QrCode2Icon sx={{ fontSize: 20, verticalAlign: 'middle' }} />
                  QR
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: "#fff" }}>#</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {links.filter(Boolean).map(link => (
                <TableRow key={link?.id || link?.short_code || Math.random()}
                  hover
                  sx={{
                    transition: "all .15s", "&:hover": { background: "#e0f7fa" }
                  }}>
                  <TableCell>
                    <Stack>
                      <b>{link?.link_name || "Không đặt tên"}</b>
                      {link?.note && (
                        <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                          {link.note}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={link?.original_url}>
                      <a href={link?.original_url || "#"} target="_blank" rel="noopener noreferrer"
                        style={{ color: "#1976d2", fontWeight: 600, wordBreak: "break-all" }}>
                        {link?.original_url
                          ? (link.original_url.length > 50
                            ? link.original_url.slice(0, 50) + "..."
                            : link.original_url)
                          : ""}
                      </a>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <a href={`${config.BASE_URL}/${link?.short_code || ""}`} target="_blank" rel="noopener noreferrer"
                        style={{ color: "#0288d1", wordBreak: "break-all", fontWeight: 600 }}>
                        {config.BASE_URL}/{link?.short_code || ""}
                      </a>
                      <Tooltip title="Copy link rút gọn">
                        <IconButton size="small"
                          onClick={() => handleCopy(`${config.BASE_URL}/${link?.short_code || ""}`)}
                          sx={{
                            color: "#00c853",
                            borderRadius: 2,
                            background: "#e0f2f1",
                            "&:hover": { background: "#c8e6c9" }
                          }}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={link?.click_count || 0}
                      color="primary"
                      icon={<ClickIcon sx={{ fontSize: 18 }} />}
                      sx={{ fontWeight: 700, bgcolor: "#dbeafe", color: "#1e40af" }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={{ fontWeight: 500 }}>
                      {link?.created_at ? String(link.created_at).substring(0, 10) : ''}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {link?.short_code && (
                      <Tooltip title="Mã QR">
                        <span>
                          <QRCodeCanvas value={`${config.BASE_URL}/${link.short_code}`} size={32} />
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Chỉnh sửa">
                      <IconButton size="small" onClick={() => handleEdit(link)} sx={{ color: "#ffc107" }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Xóa link">
                      <IconButton size="small" onClick={() => handleDelete(link?.id)} sx={{ color: "#e53935" }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
      <Snackbar
        open={snack.open}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.severity} sx={{ width: '100%' }}>{snack.text}</Alert>
      </Snackbar>

      {/* Modal chỉnh sửa link */}
      <Dialog open={!!editLink} onClose={() => setEditLink(null)}>
        <DialogTitle>Chỉnh sửa link</DialogTitle>
        <DialogContent sx={{ minWidth: 350 }}>
          <TextField
            label="Tên hiển thị"
            fullWidth sx={{ mb: 2 }}
            value={editForm.link_name}
            onChange={e => setEditForm(f => ({ ...f, link_name: e.target.value }))}
          />
          <TextField
            label="Link gốc"
            fullWidth sx={{ mb: 2 }}
            value={editForm.original_url}
            onChange={e => setEditForm(f => ({ ...f, original_url: e.target.value }))}
          />
          <TextField
            label="Ghi chú"
            fullWidth sx={{ mb: 2 }}
            value={editForm.note}
            onChange={e => setEditForm(f => ({ ...f, note: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditLink(null)} color="secondary">Hủy</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={saving}>
            Lưu
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
